// -----------------------------
// LOGIN WRAPPER COMPONENT
// -----------------------------
// This component checks if a user is logged in, validates their access level,
// and redirects them as needed. It also stores login details for future use.

// -----------------------------
// IMPORTS
// -----------------------------
// Brings in tools from React, Auth0 (login service), app context, and helper functions.
import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { CMSContext } from "@";
import * as helpers from "./helpers";
import { routesFlat } from "@routes";

export default function Login({ children, requiredAccess = ["PUBLIC"] }) {
    // -----------------------------
    // AUTH0 LOGIN AND USER INFO
    // -----------------------------
    // Grabs login status, user info, and login tools from Auth0.
    const {
        isAuthenticated,
        isLoading,
        user,
        loginWithRedirect,
        getIdTokenClaims,
    } = useAuth0();

    const { pathname } = window.location;
    const { setState } = useContext(CMSContext);
    const navigate = useNavigate();

    // -----------------------------
    // INITIAL TOKEN CHECK
    // -----------------------------
    // On first load, this checks if a saved token exists and is still valid.
    // If not, it removes old data and triggers login.
    useEffect(() => {
        console.log("Check token...");
        let token = localStorage.getItem("token");

        if ((!token || !helpers.isTokenValid(token)) && !isAuthenticated) {
            console.log("Token expired or not found, redirecting to login...");
            localStorage.removeItem("token");
            localStorage.removeItem("username");

            triggerLogin();
        }
    }, []);

    // -----------------------------
    // AUTHENTICATE & SETUP USER
    // -----------------------------
    // Once login is complete, this runs to:
    // 1. Save or refresh the login token
    // 2. Check if the user has permission to access the page
    // 3. Store login state in the app
    useEffect(() => {
        const authenticateUser = async () => {
            try {
                if (isLoading) return; // Wait until Auth0 is ready

                if (isAuthenticated && user) {
                    console.log("User authenticated:", user);
                    let token = localStorage.getItem("token");

                    // Get a new token if it's missing or expired
                    if (!token || !helpers.isTokenValid(token)) {
                        console.log("Generating new token...");
                        const idTokenClaims = await getIdTokenClaims();
                        token = idTokenClaims.__raw;

                        localStorage.setItem("token", token);
                        localStorage.setItem("username", user.name);
                    }

                    // -----------------------------
                    // CHECK USER'S ACCESS LEVEL
                    // -----------------------------
                    const isAdmin = await helpers.isUserAdmin(token);
                    const userObj = await helpers.getEmailFromToken(token);
                    const userEmail = userObj.data.email;
                    const fetchedUser = await helpers.getUserFromEmail(
                        userEmail,
                        token,
                    );

                    const fetchedUserType = fetchedUser.data.user_type;
                    const isCTreesAdmin = fetchedUserType === "ADMIN";
                    const isCTreesUser =
                        isCTreesAdmin || fetchedUserType === "INTERNAL";
                    const adminStatus = !!(isAdmin && isCTreesUser);

                    // -----------------------------
                    // CHECK IF USER HAS PAGE ACCESS
                    // -----------------------------
                    let hasAccess =
                        requiredAccess.length === 0 ||
                        requiredAccess.includes("PUBLIC") ||
                        (pathname === routesFlat.cms && isCTreesAdmin) ||
                        requiredAccess.includes(fetchedUserType) ||
                        isCTreesUser;

                    // If in development mode, restrict access to non-CTrees users
                    // We set this apart because in production, we want to allow all users
                    if (process.env.REACT_APP_LOGIN_ENABLED === "true" && !isCTreesUser) {
                        hasAccess = false; 
                    }

                    if (!hasAccess) {
                        navigate(routesFlat.noAccess); // Redirect if not allowed
                        return;
                    }

                    // -----------------------------
                    // CHECK FOR ORGANIZATION SETUP
                    // -----------------------------
                    // If a user hasn't finished setting up their organization info, redirect them to complete it
                    if (
                        !fetchedUser.data.organization_name &&
                        pathname !== routesFlat.orgSignUp
                    ) {
                        console.log(
                            "No organization found for user. After submitting up, redirect to:",
                            pathname,
                        );
                        navigate(routesFlat.orgSignUp, {
                            state: { redirectTo: pathname },
                        });
                    }

                    // -----------------------------
                    // SAVE USER STATE IN APP
                    // -----------------------------
                    setState({
                        token,
                        username: user.name,
                        userType: fetchedUserType,
                        isAdmin: adminStatus,
                    });
                } else if (!localStorage.getItem("token")) {
                    console.log("No token found... triggering login.");
                    triggerLogin(); // Re-login if somehow we have no token
                }
            } catch (error) {
                console.error("Authentication error:", error);
                triggerLogin(); // Error? Force login again
            }
        };

        authenticateUser();
    }, [
        isAuthenticated,
        isLoading,
        user,
        getIdTokenClaims,
        navigate,
        pathname,
    ]);

    // -----------------------------
    // TRIGGER LOGIN FLOW
    // -----------------------------
    // This sends the user to the login screen and then back to where they started
    const triggerLogin = () => {
        loginWithRedirect({
            appState: { returnTo: pathname },
            redirectUri: `${process.env.REACT_APP_HOME_URL}/callback`,
        });
    };

    // -----------------------------
    // RETURN CHILD COMPONENTS
    // -----------------------------
    // If user is logged in and has access, show whatever was wrapped by <Login>
    return children;
}
